import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Calculator, Ruler, Info, RotateCcw, Save, Loader2,
  AlertTriangle, CheckCircle2, Building2, Car, TreePine,
  Layers, Shield, FileText,
} from 'lucide-react';

import { laudoApi } from '../../services/laudoApi';

/**
 * ============================================================
 * COMPONENTE: VistoriaInterna_TabelaAreas
 * ------------------------------------------------------------
 * Cálculo de Área Equivalente conforme NBR 12.721.
 *
 * FÓRMULA:
 *   A_eq = Σ (A_i × C_i)
 *   Onde C = coeficiente de equivalência do tipo de área
 *
 * COEFICIENTES (NBR 12.721):
 *   Privativa   = 1.00
 *   Varanda     = 0.75
 *   Terraço     = 0.75
 *   Garagem     = 0.50
 *   Comum       = 0.20
 *
 * VERSÃO: 2.0.0
 * ============================================================
 */

const COEFICIENTES = Object.freeze({
  privativa: { valor: 1.00, label: 'Privativa Coberta', cor: 'text-blue-400',  icon: Building2 },
  varanda:   { valor: 0.75, label: 'Varanda / Sacada',  cor: 'text-purple-400', icon: TreePine  },
  terraco:   { valor: 0.75, label: 'Terraço Descoberto',cor: 'text-orange-400', icon: Layers    },
  garagem:   { valor: 0.50, label: 'Garagem / Vaga',    cor: 'text-emerald-400', icon: Car      },
  comum:     { valor: 0.20, label: 'Área Comum',        cor: 'text-amber-400',  icon: Building2 },
});

const AREAS_INICIAIS = {
  privativa: 0,
  varanda: 0,
  terraco: 0,
  garagem: 0,
  comum: 0,
};

/* =========================
   COMPONENTE
========================= */

export default function VistoriaInterna_TabelaAreas({
  reportNumber = 'REPORT-2026-MH-001',
  peritoAtivo = 'MONTE_HERMOM_USER',
  areas: areasControladas,
  onChange,
  infoLaudo = {},
  onPersistido,
}) {
  const [areas, setAreas] = useState(areasControladas || AREAS_INICIAIS);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const [mensagem, setMensagem] = useState(null);

  /* ---------- sincronização com prop ---------- */
  useEffect(() => {
    if (areasControladas && areasControladas !== areas) {
      setAreas(areasControladas);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areasControladas]);

  /* ---------- atualização de campo ---------- */
  const atualizarArea = useCallback((chave, valor) => {
    const numero = Math.max(0, parseFloat(valor) || 0);
    setAreas(prev => {
      const novo = { ...prev, [chave]: numero };
      if (typeof onChange === 'function') onChange(novo);
      return novo;
    });
  }, [onChange]);

  /* ---------- reset ---------- */
  const resetar = useCallback(() => {
    const zerado = Object.keys(AREAS_INICIAIS).reduce((acc, k) => {
      acc[k] = 0;
      return acc;
    }, {});
    setAreas(zerado);
    if (typeof onChange === 'function') onChange(zerado);
    setErro(null);
    setMensagem('Campos zerados.');
    setTimeout(() => setMensagem(null), 2000);
  }, [onChange]);

  /* ---------- persistência ---------- */
  const persistir = useCallback(async () => {
    setSalvando(true);
    setErro(null);
    setMensagem(null);

    try {
      await laudoApi.salvarModulo({
        report_id: reportNumber,
        perito: peritoAtivo,
        modulo: 'AREAS_EQUIVALENTES',
        timestamp: new Date().toISOString(),
        areas,
        areaEquivalente: calcularAreaEquivalente(areas),
        areaSoma: calcularSomaReal(areas),
      });
      setMensagem('Áreas salvas com sucesso.');
      setTimeout(() => setMensagem(null), 3000);
      if (typeof onPersistido === 'function') onPersistido(areas);
    } catch (err) {
      if (err._enfileirado) {
        setErro('Sem conexão — dados salvos localmente.');
      } else {
        setErro(err.message || 'Falha ao salvar.');
      }
    } finally {
      setSalvando(false);
    }
  }, [areas, reportNumber, peritoAtivo, onPersistido]);

  /* ---------- cálculos ---------- */
  const somaReal = useMemo(
    () => Object.values(areas).reduce((s, v) => s + (Number(v) || 0), 0),
    [areas]
  );

  const areaEquivalente = useMemo(() => {
    return Object.entries(areas).reduce((total, [chave, valor]) => {
      const coef = COEFICIENTES[chave]?.valor || 0;
      return total + (Number(valor) || 0) * coef;
    }, 0);
  }, [areas]);

  const percentualReducao = useMemo(() => {
    if (somaReal === 0) return 0;
    return ((1 - areaEquivalente / somaReal) * 100);
  }, [somaReal, areaEquivalente]);

  const temDados = somaReal > 0;

  /* =========================
     RENDER
  ========================= */

  return (
    <div className="bg-slate-900 rounded-[3rem] border border-white/5 overflow-hidden shadow-2xl text-slate-200">

      {/* HEADER */}
      <header className="bg-slate-950 p-6 lg:p-8 flex flex-wrap justify-between items-center gap-4 border-b border-white/5">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-amber-400 rounded-2xl flex items-center justify-center shadow-lg shrink-0">
            <Calculator size={22} className="text-slate-950" />
          </div>
          <div>
            <span className="text-[9px] text-slate-500 tracking-[0.4em] block">
              NBR 12.721 · COEFICIENTES DE EQUIVALÊNCIA
            </span>
            <h2 className="text-white text-[16px] tracking-tight leading-tight mt-0.5">
              Área Equivalente
            </h2>
          </div>
        </div>

        <button
          type="button"
          onClick={resetar}
          disabled={!temDados || salvando}
          className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:bg-white/10 text-[10px] tracking-widest flex items-center gap-2 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <RotateCcw size={12} />
          ZERAR
        </button>
      </header>

      {/* ERRO / MENSAGEM */}
      {erro && (
        <div className="bg-red-950/50 border-y border-red-500/20 px-6 lg:px-8 py-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-red-400 shrink-0 mt-0.5" />
          <span className="text-red-200 text-[11px] tracking-wide leading-relaxed normal-case flex-1">
            {erro}
          </span>
        </div>
      )}
      {mensagem && (
        <div className="bg-emerald-950/50 border-y border-emerald-500/20 px-6 lg:px-8 py-4 flex items-center gap-3">
          <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />
          <span className="text-emerald-200 text-[11px] tracking-wide normal-case">
            {mensagem}
          </span>
        </div>
      )}

      {/* GRID DE INPUTS */}
      <div className="p-6 lg:p-8 space-y-4">
        {Object.entries(COEFICIENTES).map(([chave, config]) => {
          const Icone = config.icon;
          const valor = areas[chave] || 0;
          const equivalente = +(valor * config.valor).toFixed(2);

          return (
            <div
              key={chave}
              className="bg-slate-950 rounded-2xl border border-white/5 p-4 flex flex-wrap items-center gap-4"
            >
              {/* ÍCONE + LABEL */}
              <div className="flex items-center gap-4 flex-1 min-w-[220px]">
                <div className="w-11 h-11 rounded-xl bg-slate-900 flex items-center justify-center shrink-0">
                  <Icone size={18} className={config.cor} />
                </div>
                <div className="min-w-0">
                  <span className="text-white text-[12px] tracking-wide block">
                    {config.label}
                  </span>
                  <span className="text-slate-500 text-[9px] tracking-widest font-mono">
                    COEF. {config.valor.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* INPUT */}
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={valor || ''}
                  onChange={(e) => atualizarArea(chave, e.target.value)}
                  placeholder="0.00"
                  className="w-32 h-11 bg-slate-900 border border-white/10 rounded-xl px-4 text-white text-[13px] text-right font-mono outline-none focus:border-amber-400 transition-all placeholder:text-slate-600"
                  disabled={salvando}
                />
                <span className="text-slate-500 text-[11px] tracking-widest font-mono">
                  m²
                </span>
              </div>

              {/* EQUIVALENTE */}
              <div className="min-w-[110px] text-right">
                <span className="text-[9px] text-slate-500 tracking-widest block">
                  EQUIVALENTE
                </span>
                <span className="text-emerald-400 text-[13px] font-mono">
                  {equivalente.toFixed(2)} m²
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* PAINEL DE RESULTADO */}
      <section className="bg-[#2F353B] p-6 lg:p-8 border-t border-white/5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* SOMA REAL */}
          <div className="bg-slate-950 rounded-2xl p-5 border border-white/5">
            <span className="text-[9px] text-slate-500 tracking-[0.3em] block mb-2">
              SOMA BRUTA
            </span>
            <span className="text-white text-[24px] font-mono tracking-tight">
              {somaReal.toFixed(2)} m²
            </span>
          </div>

          {/* ÁREA EQUIVALENTE */}
          <div className="bg-amber-400 rounded-2xl p-5 shadow-lg">
            <span className="text-[9px] text-slate-950/60 tracking-[0.3em] block mb-2">
              ÁREA EQUIVALENTE FINAL
            </span>
            <span className="text-slate-950 text-[28px] font-mono tracking-tight leading-none">
              {areaEquivalente.toFixed(2)} m²
            </span>
          </div>

          {/* REDUÇÃO */}
          <div className="bg-slate-950 rounded-2xl p-5 border border-white/5">
            <span className="text-[9px] text-slate-500 tracking-[0.3em] block mb-2">
              REDUÇÃO POR COEFICIENTES
            </span>
            <span className={`text-[24px] font-mono tracking-tight ${
              percentualReducao > 0 ? 'text-blue-400' : 'text-slate-500'
            }`}>
              {percentualReducao > 0 ? '−' : ''}
              {percentualReducao.toFixed(2)}%
            </span>
          </div>
        </div>

        {/* FÓRMULA */}
        <div className="mt-6 bg-slate-950 rounded-2xl p-4 border border-white/5 flex items-start gap-4">
          <Info size={16} className="text-amber-400 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <span className="text-[9px] text-amber-400 tracking-[0.3em] block">
              FUNDAMENTAÇÃO MATEMÁTICA
            </span>
            <p className="text-slate-400 text-[11px] font-mono normal-case">
              A_eq = Σ (A_i × C_i) = {Object.entries(areas)
                .filter(([, v]) => v > 0)
                .map(([k, v]) => `${v}×${COEFICIENTES[k].valor}`)
                .join(' + ') || '0'}
            </p>
          </div>
        </div>

        {/* AÇÃO */}
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={persistir}
            disabled={!temDados || salvando}
            className="bg-amber-400 text-slate-950 px-8 py-4 rounded-2xl text-[11px] tracking-widest flex items-center gap-3 hover:bg-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {salvando ? (
              <><Loader2 size={16} className="animate-spin" /> SALVANDO...</>
            ) : (
              <><Save size={16} /> SALVAR ÁREAS</>
            )}
          </button>
        </div>
      </section>

      {/* RODAPÉ NORMATIVO */}
      <footer className="p-6 lg:p-8 bg-slate-950 border-t border-white/5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Shield size={14} className="text-amber-400/60" />
          <span className="text-[9px] text-slate-500 tracking-[0.3em]">
            METODOLOGIA BASEADA NA NBR 12.721
          </span>
        </div>
        <div className="flex items-center gap-2 text-slate-500 text-[9px] tracking-widest font-mono">
          <FileText size={12} />
          <span>ID: {reportNumber}</span>
        </div>
      </footer>
    </div>
  );
}

/* =========================
   HELPERS EXPORTÁVEIS
========================= */

export function calcularAreaEquivalente(areas = {}) {
  return +Object.entries(areas).reduce((total, [chave, valor]) => {
    const coef = COEFICIENTES[chave]?.valor || 0;
    return total + (Number(valor) || 0) * coef;
  }, 0).toFixed(2);
}

export function calcularSomaReal(areas = {}) {
  return +Object.values(areas)
    .reduce((s, v) => s + (Number(v) || 0), 0)
    .toFixed(2);
}

export { COEFICIENTES };